import T, { useTranslate } from '@/components/Helpers/Translate'
import { getMarketConfig } from '@/constants/MarketExceptions/utils/getMarketConfig'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import useClientRedirect from '@/lib/hooks/routing/useRedirect'
import { useSignOutUser } from '@/lib/hooks/services/useSignOutUser'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { cn } from '@/lib/utils'
import generateOfficeRedirectURL from '@/lib/utils/shared/generateOfficeRedirectURL'
import generateBecomeAffiliateUrl from '@/lib/utils/shared/generateBecomeAffiliateUrl'
import type { UserType } from '@/store/types/user'
import useGlobalStore from '@/store/useGlobalStore'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

type AccountLink = {
  path: string | null
  title: string
  disabled?: ({ user }: { user: UserType }) => boolean
}

type AccountLinksType = {
  primary: AccountLink[]
  secondary: AccountLink[]
}

interface MenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  type: 'primary' | 'secondary'
  isActive?: boolean
  children: React.JSX.Element | React.JSX.Element[]
}

export const ACCOUNT_LINKS: AccountLinksType = {
  primary: [
    {
      path: 'personal-details',
      title: 'personal_details',
    },
    {
      path: 'payment-methods',
      title: 'payment_methods',
      disabled: ({ user }: { user: UserType }) => {
        if (!user.customer) return true

        const country = user.customer.mainAddress?.country
        const marketException = getMarketConfig(country)

        // If no market exception exists, disable payment methods
        if (!marketException) return true

        // Disable if hasNativeCreditCard is not explicitly true (false or undefined)
        return marketException.hasNativeCreditCard !== true
      },
    },
    {
      path: 'shipping-addresses',
      title: 'shipping_addresses',
    },
    {
      path: 'order-history',
      title: 'order_history',
    },
    {
      path: 'subscriptions',
      title: 'subscriptions',
      disabled: ({ user }: { user: UserType }) => {
        if (!user.customer) return false

        return (
          getMarketConfig(user.customer.mainAddress?.country)
            ?.omitSubscriptionsAppWide ?? false
        )
      },
    },
    {
      path: 'invite',
      title: 'referral_link',
    },
    {
      path: null,
      title: 'my_office',
      disabled: ({ user }: { user: UserType }) =>
        user.customerType !== 'Associate',
    },
  ],
  secondary: [
    {
      path: 'affiliate',
      title: 'become_affiliate',
      disabled: ({ user }: { user: UserType }) =>
        user.customerType !== 'Member',
    },
    {
      path: 'signout',
      title: 'signout',
    },
  ],
}

const MenuItem = ({ type, children, isActive, ...props }: MenuItemProps) => (
  <div
    className={cn(
      'mt-1 flex cursor-pointer justify-between rounded-lg p-3 hover:bg-slate-100',
      isActive && 'bg-slate-200',
    )}
    {...props}
  >
    {children}
  </div>
)

const AccountMenuItems = ({
  isAccountPopover = false,
}: {
  isAccountPopover?: boolean
}) => {
  const { translate } = useTranslate()
  const {
    user,
    app: { alpha2, marketExceptions },
  } = useGlobalStore()
  const { signOutUser } = useSignOutUser()
  const router = useRouter()
  const { clientRedirect } = useClientRedirect()
  const { gtm } = useGTM()

  const [primary, setPrimary] = useState<AccountLink[]>([])

  useEffect(() => {
    setPrimary(
      ACCOUNT_LINKS.primary.filter((item) => !item.disabled?.({ user })),
    )
  }, [user])

  return (
    <div className="mt-10 w-full">
      {primary
        .filter((item) => !item.disabled?.({ user }))
        .map((item) => {
          const isMenuActive = item.path
            ? router.asPath.includes(item.path)
            : false

          return (
            <MenuItem
              isActive={isMenuActive}
              type="primary"
              key={item.path}
              onClick={() => {
                if (!window) return

                if (item.title === 'my_office') {
                  window
                    .open(
                      generateOfficeRedirectURL({
                        user,
                        currentCountry: alpha2,
                        marketExceptions,
                      }),
                    )
                    ?.focus()
                  return
                }

                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                clientRedirect.to({ path: `/account/${item.path}` })
                gtm.userMenuNavigationClicked(
                  item.title,
                  isAccountPopover ? 'profile_menu' : 'account_menu',
                )

                mixpanelService
                  .trackEvent(MixpanelEvent.USER_MENU_NAVIGATION_CLICKED, {
                    action: item.title,
                    event_location: isAccountPopover
                      ? 'profile_menu'
                      : 'account_menu',
                  })
                  .catch(() => {})
              }}
              tabIndex={0}
            >
              <p>{translate(item.title)}</p>
              <Image
                alt="Arrow icon"
                height="8"
                src="/icons/arrow.svg"
                width="5"
              />
            </MenuItem>
          )
        })}
      <div className="my-3 border-t border-gray-200" />
      {ACCOUNT_LINKS.secondary
        .filter((item) => !item.disabled?.({ user }))
        .map((item) => (
          <MenuItem
            type="secondary"
            data-test={item.title}
            key={item.path}
            onClick={() => {
              if (item.title === 'signout') {
                ;(async () => {
                  await signOutUser()
                })().catch(() => {})
              } else if (item.title === 'become_affiliate') {
                gtm.userMenuNavigationClicked(
                  'become_a_distributor',
                  isAccountPopover ? 'profile_menu' : 'account_menu',
                )

                mixpanelService
                  .trackEvent(MixpanelEvent.USER_MENU_NAVIGATION_CLICKED, {
                    action: 'become_a_distributor',
                    event_location: isAccountPopover
                      ? 'profile_menu'
                      : 'account_menu',
                  })
                  .catch(() => {})
                window.open(
                  generateBecomeAffiliateUrl({
                    user,
                    currentCountry: alpha2,
                    marketExceptions,
                  }),
                  '_blank',
                )
              }
            }}
            tabIndex={0}
          >
            <p>
              <T>{item.title}</T>
            </p>
          </MenuItem>
        ))}
    </div>
  )
}

export default AccountMenuItems
